"use client";

import { useEffect, useRef, useState } from "react";
import { Dialog, DialogClose, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { captureClientEvent } from "@/lib/analytics/client";
import { useCreatorReferralReward } from "@/hooks/useCreatorReferralReward";
import { useBoard } from "@/components/BoardProvider";
import { useAction, useConvexAuth, useQuery } from "convex/react";
import { api } from "@repo/backend/convex/_generated/api";
import { HugeiconsIcon } from "@hugeicons/react";
import { Cancel01Icon } from "@hugeicons/core-free-icons";
import { PlanSelector } from "@/components/pricing/PlanSelector";
import { SaleBanner } from "@/components/pricing/SaleBanner";
import {
  PASS_STATS,
  formatPrice,
  getPassBenefits,
  getPassPlans,
  priceAfterAward,
} from "@/components/pricing/planContent";

export interface PricingModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  defaultTab?: "subscription" | "payg";
  trigger?: string;
  context?: Record<string, unknown>;
}

export function PricingModal({ open, setOpen, defaultTab = "subscription", trigger, context }: PricingModalProps) {
  const board = useBoard();
  const createCheckout = useAction(api.autumn.createCheckout);
  const {
    loading: creatorRewardLoading,
    shouldApply: shouldApplyCreatorReward,
    rewardId: creatorRewardId,
  } = useCreatorReferralReward();
  // The reward is forced on server-side at checkout; this only makes the price
  // the holder is about to pay the price they see beforehand.
  const { isAuthenticated } = useConvexAuth();
  const awardPercent = useQuery(api.scholarships.getMyAward, isAuthenticated ? {} : "skip");
  const closeReasonRef = useRef<"purchase" | null>(null);
  const hasTrackedOpenRef = useRef<boolean>(false);

  const checkoutRewardId =
    !creatorRewardLoading && shouldApplyCreatorReward && creatorRewardId
      ? creatorRewardId
      : undefined;

  const [pendingPlan, setPendingPlan] = useState<string | null>(null);
  const [selected, setSelected] = useState<string>("pass_lifetime");
  const plans = getPassPlans();

  const onChooseProduct = async (uiPlanId: string) => {
    if (pendingPlan) return;
    const plan = plans.find((p) => p.id === uiPlanId) ?? plans[0];
    setPendingPlan(uiPlanId);
    closeReasonRef.current = "purchase";
    const ret = typeof window !== "undefined" ? encodeURIComponent(window.location.href) : undefined;
    const successUrl = typeof window !== "undefined" && ret ? `${window.location.origin}/checkout/success?return=${ret}` : undefined;
    captureClientEvent("pricing_subscription_checkout_clicked", {
      productId: plan.checkoutId,
      billingPeriod: plan.id === "pass_weekly" ? "weekly" : "lifetime",
      price: plan.amount,
      source: "pricing_modal",
    });
    try {
      const { paymentUrl } = await createCheckout({
        planId: plan.checkoutId,
        successUrl,
        rewardId: checkoutRewardId,
      });
      if (paymentUrl) {
        window.location.href = paymentUrl;
      } else if (successUrl) {
        // Applied without payment (e.g. instant upgrade) — go celebrate.
        window.location.href = successUrl;
      }
    } catch (e) {
      console.error("Checkout failed", e);
      setPendingPlan(null);
    }
    // On success we redirect away, so leave the button in its loading state.
  };

  useEffect(() => {
    if (open) {
      hasTrackedOpenRef.current = true;
      captureClientEvent("pricing_modal_opened", { defaultTab, source: "paywall", trigger, ...context });
      closeReasonRef.current = null;
    }
  }, [open, defaultTab, trigger, context]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && hasTrackedOpenRef.current) {
      captureClientEvent("pricing_modal_closed", { source: "paywall", trigger, ...context });
      if (closeReasonRef.current !== "purchase") {
        captureClientEvent("pricing_modal_dismissed", { source: "paywall", trigger, ...context });
      }
      closeReasonRef.current = null;
      hasTrackedOpenRef.current = false;
    }
    setOpen(nextOpen);
  };

  const handleSkip = () => {
    captureClientEvent("pricing_modal_skipped", { source: "paywall", trigger, ...context });
    setOpen(false);
  };

  const isTutorialMode = trigger === "tutorial";

  // Contextual header copy: reflect what the user was doing when the paywall
  // fired, so the ask reads as "finish what you started", not a generic wall.
  // Falls back to a general pitch for unknown triggers.
  const CONTEXT_COPY: Record<string, { title: string; subtitle: string }> = {
    exam: {
      title: "Finish the paper and see your predicted band",
      subtitle: `Full ${board.shortName} papers, timed and marked, with the worked solution for every question.`,
    },
    practice: {
      title: "Keep practising, past the daily limit",
      subtitle: "Unlimited question bank and adaptive practice, tuned to the topics you keep getting wrong.",
    },
    practice_review: {
      title: "Drill the topics you keep getting wrong",
      subtitle: "Unlimited targeted practice on your weakest areas, marked with full worked solutions.",
    },
    practice_pack: {
      title: "Build unlimited practice packs",
      subtitle: "Generate as many focused practice sets as you want, on any topic.",
    },
    qotd_practice_similar: {
      title: "Practise more questions like today's",
      subtitle: "Unlimited practice across every topic, with the worked solution for each one.",
    },
    qotd_archive: {
      title: "Unlock the full question archive",
      subtitle: "Every past question of the day, plus the entire question bank.",
    },
    textbook_topic_detail: {
      title: "Practise every topic as you learn it",
      subtitle: "Unlimited practice and full worked solutions alongside every textbook page.",
    },
    textbook_practice_callout: {
      title: "Practise every topic as you learn it",
      subtitle: "Unlimited practice and full worked solutions alongside every textbook page.",
    },
  };

  const contextCopy = (trigger && CONTEXT_COPY[trigger]) || {
    title: `Everything you need for ${board.shortName}`,
    subtitle: "Every past paper, the full question bank, and worked solutions, unlocked today.",
  };

  const headerTitle = isTutorialMode ? "Unlock the full platform" : contextCopy.title;
  const headerSubtitle = isTutorialMode
    ? `Every week before ${board.shortName} exam day counts. Start with everything unlocked today.`
    : contextCopy.subtitle;

  const BENEFITS = getPassBenefits(board.shortName);
  const STATS = PASS_STATS;

  const selectedPlan = plans.find((p) => p.id === selected) ?? plans[0];

  const planCards = (
    <PlanSelector plans={plans} selected={selected} onSelect={setSelected} awardPercent={awardPercent} />
  );

  const ctaBlock = (
    <>
      <Button
        className="h-14 w-full rounded-xl text-base font-semibold shadow-sm transition-shadow hover:shadow-md md:h-16 md:text-lg"
        onClick={() => onChooseProduct(selected)}
        loading={pendingPlan === selected}
        disabled={pendingPlan !== null}
      >
        {selectedPlan.id === "pass_lifetime" ? "Get lifetime access for " : "Start weekly access for "}
        {formatPrice(priceAfterAward(selectedPlan.amount, awardPercent))}
      </Button>
      <div className="mt-3 text-center">
        {awardPercent ? (
          <p className="text-xs text-muted-foreground">
            Your {awardPercent}% scholarship is on your account and comes off at checkout.
          </p>
        ) : null}
      </div>
      {/* Always offered. The close control alone is a 36px icon on a full-screen
          modal, so without this the only visible way out is one people miss. */}
      <div className="mt-3 text-center">
        <button
          type="button"
          onClick={handleSkip}
          className="rounded-md px-2 py-1 text-sm text-muted-foreground underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          Skip for now
        </button>
      </div>
    </>
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        showCloseButton={false}
        className={cn(
          "inset-0 left-0 top-0 h-[100dvh] w-screen max-w-none translate-x-0 translate-y-0",
          "block gap-0 overflow-hidden rounded-none border-0 p-0 text-foreground",
        )}
      >
        <DialogClose
          aria-label="Close"
          className="absolute right-4 top-4 z-20 flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-white transition-colors hover:bg-white/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 md:right-5 md:top-5 md:bg-muted md:text-muted-foreground md:hover:bg-muted md:hover:text-foreground md:focus-visible:ring-ring"
        >
          <HugeiconsIcon icon={Cancel01Icon} size={20} strokeWidth={1.8} />
        </DialogClose>

        {/* Mobile: one cohesive column. Title + plan chooser sit above the fold,
            benefits/stats scroll below, and the CTA stays pinned to the bottom. */}
        <div className="flex h-full flex-col md:hidden">
          <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-6 pt-14">
            <h2 className="text-2xl font-semibold leading-snug">{headerTitle}</h2>
            <p className="mt-1.5 text-sm text-muted-foreground">{headerSubtitle}</p>

            <div className="mt-5">{planCards}</div>

            <div className="mt-8">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                What&apos;s included
              </p>
              <ul className="mt-3 space-y-3">
                {BENEFITS.map((b) => (
                  <li key={b.title} className="flex gap-3">
                    <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10">
                      <HugeiconsIcon icon={b.icon} size={18} strokeWidth={1.8} className="text-primary" />
                    </span>
                    <span>
                      <span className="block text-sm font-medium text-foreground">{b.title}</span>
                      <span className="block text-xs text-muted-foreground">{b.desc}</span>
                    </span>
                  </li>
                ))}
              </ul>

              <div className="mt-6 grid grid-cols-3 gap-3 border-t border-border pt-5">
                {STATS.map((s) => (
                  <div key={s.label}>
                    <p className="text-xl font-bold leading-none text-foreground">{s.value}</p>
                    <p className="mt-1 text-[11px] leading-tight text-muted-foreground">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex-shrink-0 border-t border-border bg-background px-5 pt-3 pb-[calc(env(safe-area-inset-bottom,0px)+1rem)]">
            {ctaBlock}
          </div>
        </div>

        {/* Desktop: two-column branded value panel + plan selection. */}
        <div className="hidden h-full md:grid md:grid-cols-2 md:overflow-hidden">
          <div className="flex flex-col bg-primary p-6 text-white md:justify-center md:overflow-y-auto md:p-12">
            <div className="mx-auto w-full max-w-md">
              <DialogTitle className="text-2xl font-semibold leading-snug text-white md:text-3xl">
                Unlock{" "}
                <span className="underline decoration-white/50 decoration-4 underline-offset-4">
                  unlimited
                </span>{" "}
                practice and every worked solution
              </DialogTitle>
              <p className="mt-2 text-sm text-white/70">{headerSubtitle}</p>

              <ul className="mt-6 space-y-3.5 md:mt-8 md:space-y-4">
                {BENEFITS.map((b) => (
                  <li key={b.title} className="flex gap-3">
                    <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-white/10">
                      <HugeiconsIcon icon={b.icon} size={20} strokeWidth={1.8} className="text-white" />
                    </span>
                    <span>
                      <span className="block text-sm font-semibold text-white">{b.title}</span>
                      <span className="block text-xs text-white/65">{b.desc}</span>
                    </span>
                  </li>
                ))}
              </ul>

              <div className="mt-7 grid grid-cols-3 gap-3 border-t border-white/15 pt-6">
                {STATS.map((s) => (
                  <div key={s.label}>
                    <p className="text-2xl font-bold leading-none text-white">{s.value}</p>
                    <p className="mt-1.5 text-xs leading-tight text-white/65">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex flex-col p-6 md:justify-center md:overflow-y-auto md:p-8">
            <div className="mx-auto w-full max-w-md">
              <SaleBanner plans={plans} className="mb-5" />
              <div>
                <h2 className="text-xl font-semibold">Get full access until your {board.shortName}</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Practise unlimited questions, past papers and worked solutions.
                </p>
              </div>

              <div className="mt-5">{planCards}</div>
              <div className="mt-5">{ctaBlock}</div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
